import type { LinkProps } from '@tanstack/react-router';
import { stripMarkdown } from '@/ui/utils';
import { DocumentType, DOCUMENT_TYPE } from '@/features/documents/constants';
import { docsRegexp } from '@/features/documents/utils';
import type { CreateInsightRequest, Insight, InsightRef } from '@/features/insights/types';
import type { InsightTaskLinks } from '@/features/tasks/types';
import { SubjectKind } from '@/subjects';
import { stripImageTokens } from '@/features/chat/attachments/utils';

export const insightKey = (
  insight: Pick<InsightRef, 'resourceId' | 'subjectKind' | 'subjectId'> &
    ({ resourceType: InsightRef['resourceType'] } | { type: InsightRef['resourceType'] }),
) =>
  `${'resourceType' in insight ? insight.resourceType : insight.type}:${insight.resourceId}:${insight.subjectKind}:${insight.subjectId}`;

export const documentTypeFromEvalType = (evalType: DocumentType): DocumentType => {
  if (evalType === DOCUMENT_TYPE.contract) return DOCUMENT_TYPE.contract;
  if (evalType === DOCUMENT_TYPE.bid) return DOCUMENT_TYPE.bid;
  if (evalType === DOCUMENT_TYPE.project) return DOCUMENT_TYPE.project;
  return DOCUMENT_TYPE.tenderPackage;
};

export const insightMarkdown = (snapshotText: string) =>
  stripImageTokens(snapshotText.replaceAll(docsRegexp, '').replaceAll('\\n', '\n'));

export const insightText = (snapshotText: string) => stripMarkdown(insightMarkdown(snapshotText));

export const insightLabelKind = (insight: {
  subjectKind: SubjectKind | null;
  context: Insight['context'];
}) => {
  if (
    insight.subjectKind === SubjectKind.CUSTOM_TOPIC_STATEMENT ||
    insight.subjectKind === SubjectKind.CUSTOM_TOPIC_QUERY
  )
    return insight.context?.extractionId
      ? SubjectKind.CUSTOM_TOPIC_STATEMENT
      : SubjectKind.CUSTOM_TOPIC_QUERY;
  return insight.subjectKind ?? SubjectKind.CHAT;
};

export const selectionInsight = (
  insight: CreateInsightRequest,
  text: string,
): CreateInsightRequest => ({
  ...insight,
  subjectId: `${insight.subjectId}#${crypto.randomUUID()}`,
  snapshotText: text,
  context: { ...insight.context, entityId: insight.subjectId },
});

export const isChatInsight = (insight: { subjectKind: SubjectKind | null }) =>
  insight.subjectKind === SubjectKind.CHAT || insight.subjectKind === SubjectKind.CHAT_MESSAGE;

export const isPrivateChatInsight = (insight: Insight, userId: string) =>
  isChatInsight(insight) && !!insight.savedBy && insight.savedBy.userId !== userId;

export const insightLink = (insight: Insight, projectId: string): LinkProps | undefined => {
  const subjectKind = insight.subjectKind;
  const context = insight.context ?? {};
  const entityId = context.entityId ?? insight.subjectId;
  const isBid = insight.resourceType === DOCUMENT_TYPE.bid;
  const workspaceId = isBid ? insight.workspaceId : insight.resourceId;
  const bidId = insight.resourceId;

  if (isChatInsight(insight)) {
    const search = {
      threadId: context.sessionId ?? entityId,
      messageId:
        subjectKind === SubjectKind.CHAT_MESSAGE ? (context.messageId ?? entityId) : undefined,
    };
    if (isBid)
      return {
        to: '/project/$projectId/workspaces/$workspaceId/bids/$bidId',
        params: { projectId, workspaceId, bidId },
        search,
      };
    if (insight.resourceType === DOCUMENT_TYPE.project)
      return { to: '/project/$projectId', params: { projectId }, search };
    return {
      to: '/project/$projectId/workspaces/$workspaceId',
      params: { projectId, workspaceId },
      search,
    };
  }

  if (subjectKind === SubjectKind.AMBIGUITY_STATEMENT) {
    if (isBid) return;
    return {
      to: '/project/$projectId/workspaces/$workspaceId/ambiguities/$ambiguityId',
      params: { projectId, workspaceId, ambiguityId: entityId },
    };
  }

  if (subjectKind === SubjectKind.EVALUATION_CONFLICT_FINDING)
    return {
      to: '/project/$projectId/workspaces/$workspaceId/conflicts/$conflictId',
      params: { projectId, workspaceId, conflictId: entityId },
    };

  if (subjectKind === SubjectKind.CLAUSE_SCANNER_CLAUSE) {
    const subcategoryId = context.subcategoryId;
    const clauseId = context.clauseId;
    if (!subcategoryId || !clauseId) return;
    return {
      to: '/project/$projectId/workspaces/$workspaceId/clauses/$subcategoryId/$clauseId',
      params: { projectId, workspaceId, subcategoryId, clauseId },
    };
  }

  if (subjectKind === SubjectKind.STANDARD_DEVIATION_STATEMENT) {
    const deviationId = context.deviationId;
    if (!deviationId) return;
    return {
      to: '/project/$projectId/workspaces/$workspaceId/standard-deviations/$deviationId',
      params: { projectId, workspaceId, deviationId },
      search: context.statementId ? { statementId: context.statementId } : undefined,
    };
  }

  if (subjectKind === SubjectKind.BID_SUBMISSION_COVERAGE) {
    if (!context.category) return;
    if (!context.groupId)
      return {
        to: '/project/$projectId/workspaces/$workspaceId/bids/$bidId/compliance/$categoryId',
        params: { projectId, workspaceId, bidId, categoryId: context.category },
      };
    return {
      to: '/project/$projectId/workspaces/$workspaceId/bids/$bidId/compliance/$categoryId/$groupId',
      params: {
        projectId,
        workspaceId,
        bidId,
        categoryId: context.category,
        groupId: context.groupId,
      },
    };
  }

  if (subjectKind === SubjectKind.BID_CV_STATEMENT) {
    if (!context.positionId) return;
    return {
      to: '/project/$projectId/workspaces/$workspaceId/bids/$bidId/cv-analysis/$positionId',
      params: { projectId, workspaceId, bidId, positionId: context.positionId },
      search: { statementId: context.statementId },
    };
  }

  if (subjectKind === SubjectKind.STRENGTHS_WEAKNESSES_STATEMENT) {
    const assessmentSearch = { assessmentStatementId: entityId };
    if (isBid)
      return {
        to: '/project/$projectId/workspaces/$workspaceId/bids/$bidId',
        params: { projectId, workspaceId, bidId },
        search: assessmentSearch,
      };
    return {
      to: '/project/$projectId/workspaces/$workspaceId',
      params: { projectId, workspaceId },
      search: assessmentSearch,
    };
  }

  if (subjectKind === SubjectKind.BID_METHODOLOGY)
    return {
      to: '/project/$projectId/workspaces/$workspaceId/bids/$bidId/methodology',
      params: { projectId, workspaceId, bidId },
      search: { statementId: context.statementId },
    };

  if (
    subjectKind === SubjectKind.LEGAL_DEVIATION_CLUSTER ||
    subjectKind === SubjectKind.LEGAL_DEVIATION_STATEMENT
  ) {
    const domain = context.domain;
    if (!domain) return;
    return {
      to: '/project/$projectId/workspaces/$workspaceId/contract-review/$domain',
      params: { projectId, workspaceId, domain },
      search: { clusterId: context.clusterId },
    };
  }

  if (subjectKind === SubjectKind.BID_RESERVATION) {
    const procurementId = context.procurementId;
    const findingKey = context.findingKey;
    if (!procurementId || !findingKey) return;
    return {
      to: '/project/$projectId/workspaces/$workspaceId/subcontractor-bids/$procurementId/reservations',
      params: { projectId, workspaceId, procurementId },
      search: { findingKey, subcontractorId: context.subcontractorId },
    };
  }

  if (subjectKind === SubjectKind.PROCUREMENT_COVERAGE) {
    const procurementId = context.procurementId;
    const [, , requirementId, subcontractorId] = (context.entityId ?? entityId).split(':');
    if (!procurementId || !requirementId || !subcontractorId) return;
    return {
      to: '/project/$projectId/workspaces/$workspaceId/subcontractor-bids/$procurementId/leveling',
      params: { projectId, workspaceId, procurementId },
      search: {
        tab: context.tab === 'compliance' ? 'compliance' : 'scope',
        requirementId,
        subcontractorId,
      },
    };
  }

  if (subjectKind === SubjectKind.WORK_PACKAGE_COLUMN)
    return {
      to: '/project/$projectId/workspaces/$workspaceId/scope-breakdown',
      params: { projectId, workspaceId },
    };

  if (subjectKind === SubjectKind.CHECKLIST_ITEM) {
    if (isBid) return;
    const runId = context.runId;
    if (runId)
      return {
        to: '/project/$projectId/workspaces/$workspaceId/checklists/$runId',
        params: { projectId, workspaceId, runId },
        search: { itemId: entityId },
      };
    return {
      to: '/project/$projectId/workspaces/$workspaceId/checklists',
      params: { projectId, workspaceId },
    };
  }

  if (subjectKind === SubjectKind.BID_COMPARISON) {
    if (!context.sheetId) return;
    return {
      to: '/project/$projectId/workspaces/$workspaceId/bids/comparison/$sheetId',
      params: { projectId, workspaceId, sheetId: context.sheetId },
      search: { commentEntityId: entityId },
    };
  }

  if (context.extractionId && !isBid)
    return {
      to: '/project/$projectId/workspaces/$workspaceId/extractions',
      params: { projectId, workspaceId },
      search: {
        extractionId: context.extractionId,
        queryId: context.queryId,
        id: context.statementId,
      },
    };

  const summarySearch = { targetStatementId: context.targetStatementId ?? entityId };
  if (isBid)
    return {
      to: '/project/$projectId/workspaces/$workspaceId/bids/$bidId/summary',
      params: { projectId, workspaceId, bidId },
      search: summarySearch,
    };
  return {
    to: '/project/$projectId/workspaces/$workspaceId/summary',
    params: { projectId, workspaceId },
    search: summarySearch,
  };
};

export const insightRequest = (insight: Insight): CreateInsightRequest => ({
  type: insight.resourceType,
  resourceId: insight.resourceId,
  subjectKind: insight.subjectKind,
  subjectId: insight.subjectId,
  snapshotText: insight.snapshotText,
});

export const insightTasks = (links: InsightTaskLinks | undefined) => [
  ...new Map(
    [
      ...(links?.actionRequiredTask ?? []),
      ...(links?.linkedTasks.filter((task) => !task.detached) ?? []),
    ].map((task) => [task.taskId, task]),
  ).values(),
];
